import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { Toaster } from './components/ui/sonner';
import ScrollToTop from './components/ScrollToTop';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';

// Public Pages
import HomePage from './pages/HomePage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import ServicesPage from './pages/ServicesPage';
import BookingPage from './pages/BookingPage';
import BookingSuccessPage from './pages/BookingSuccessPage';
import ServiceAreaPage from './pages/ServiceAreaPage';
import ReviewsPage from './pages/ReviewsPage';
import ApprovedSitePage from './pages/ApprovedSitePage';
import FAQPage from './pages/FAQPage';
import CareersPage from './pages/CareersPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PoliciesPage from './pages/PoliciesPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SmsTermsPage from './pages/SmsTermsPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ConsultationPage from './pages/ConsultationPage';

// CRM Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminAppointmentsPage from './pages/admin/AdminAppointmentsPage';
import AdminAppointmentDetailPage from './pages/admin/AdminAppointmentDetailPage';
import AdminQCTemplatesPage from './pages/admin/AdminQCTemplatesPage';
import AdminChangeRequestsPage from './pages/admin/AdminChangeRequestsPage';
import AdminClientsPage from './pages/admin/AdminClientsPage';
import AdminCleanersPage from './pages/admin/AdminCleanersPage';
import AdminServicesPage from './pages/admin/AdminServicesPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminFAQsPage from './pages/admin/AdminFAQsPage';
import AdminDuplicatesPage from './pages/admin/AdminDuplicatesPage';
import AdminNotificationSettingsPage from './pages/admin/AdminNotificationSettingsPage';
import AdminInvoicesPage from './pages/admin/AdminInvoicesPage';
import AdminRecurringInvoicesPage from './pages/admin/AdminRecurringInvoicesPage';
import AdminSentLinksPage from './pages/admin/AdminSentLinksPage';
import AdminTimeTrackingPage from './pages/admin/AdminTimeTrackingPage';
import AdminPayoutsPage from './pages/admin/AdminPayoutsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminServiceAreasPage from './pages/admin/AdminServiceAreasPage';
import AdminReportsListPage from './pages/admin/AdminReportsListPage';
import AdminReportReviewPage from './pages/admin/AdminReportReviewPage';
import AdminSchedulePage from './pages/admin/AdminSchedulePage';
import AdminCalendarPage from './pages/admin/AdminCalendarPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminRecycleBinPage from './pages/admin/AdminRecycleBinPage';
import AdminInventoryPage from './pages/admin/AdminInventoryPage';
import AdminInventoryDetailPage from './pages/admin/AdminInventoryDetailPage';
import AdminSignInActivityPage from './pages/admin/AdminSignInActivityPage';
import AdminCustomerDetailPage from './pages/admin/AdminCustomerDetailPage';
import AdminHiringPage from './pages/admin/AdminHiringPage';
import AdminInquiriesPage from './pages/admin/AdminInquiriesPage';
import AdminInquiryPage from './pages/admin/AdminInquiryPage';
import AdminFailedEmailsPage from './pages/admin/AdminFailedEmailsPage';
import AdminApplicantPage from './pages/admin/AdminApplicantPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminAudiencesPage from './pages/admin/AdminAudiencesPage';
import AdminCommunicationPage from './pages/admin/AdminCommunicationPage';
import MyGuruPage from './pages/admin/MyGuruPage';
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage';
import AdminPerformancePage from './pages/admin/AdminPerformancePage';
import AdminQuoteRequestsPage from './pages/admin/AdminQuoteRequestsPage';
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage';
import AdminDataCleanupPage from './pages/admin/AdminDataCleanupPage';
import AdminNeedsReviewPage from './pages/admin/AdminNeedsReviewPage';
import AdminTasksPage from './pages/admin/AdminTasksPage';
import { AdminBillingOverviewPage, AdminBillingPaymentsPage } from './pages/admin/AdminBillingPages';
import AdminAnalyticsInsightsPage from './pages/admin/AdminAnalyticsInsightsPage';
import { AdminScheduleDayPage, AdminScheduleChangesPage } from './pages/admin/AdminScheduleViews';
import AdminAIWorkspacePage from './pages/admin/AdminAIWorkspacePage';
import AdminIntegrationsPage from './pages/admin/AdminIntegrationsPage';
import AdminEmailActivityPage from './pages/admin/AdminEmailActivityPage';
import ConditionAssessmentPage from './pages/admin/ConditionAssessmentPage';
import { GuruAccountPage, GuruAvailabilityPage, GuruJobsPage, GuruMessagesPage, GuruPayoutsPage, GuruSchedulePage, GuruTimePage, GuruTodayPage } from './pages/cleaner/GuruPages';
import { CleanerShell } from './components/crm/CleanerShell';
import CleanerReportPage from './pages/cleaner/CleanerReportPage';
import CleanerPerformancePage from './pages/cleaner/CleanerPerformancePage';
import ClientDashboard from './pages/portal/ClientDashboard';
import ClientCleansPage from './pages/portal/ClientCleansPage';
import ClientPropertiesPage from './pages/portal/ClientPropertiesPage';
import ClientMessagesPage from './pages/portal/ClientMessagesPage';
import ClientBillingPage from './pages/portal/ClientBillingPage';
import ClientAccountPage from './pages/portal/ClientAccountPage';
import ClientReportsListPage from './pages/portal/ClientReportsListPage';
import ClientReportPage from './pages/portal/ClientReportPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import CardCapturePage from './pages/CardCapturePage';
import IntakePage from './pages/IntakePage';
import QuoteConfirmationPage from './pages/QuoteConfirmationPage';
import IntakeCompletePage from './pages/IntakeCompletePage';

// Protected Route Components
import { AdminRoute, CleanerRoute, ClientRoute, ProtectedRoute } from './components/crm/ProtectedRoute';

// Layout wrapper for public pages
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

// Redirect after login based on role
const AuthRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F56F05]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.role) {
    case 'admin':
    case 'master_admin':
      return <Navigate to="/admin/dashboard" replace />;
    case 'cleaner':
      return <Navigate to="/cleaner/dashboard" replace />;
    case 'client':
      return <Navigate to="/portal/dashboard" replace />;
    default:
      return <Navigate to="/" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationProvider>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout><HomePage /></Layout>} />
          <Route path="/quote-received" element={<Layout><QuoteConfirmationPage /></Layout>} />
          <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
          {/* Homepage 2.0 service destinations — aliased to existing detail pages pending dedicated content */}
          <Route path="/services/home-clean" element={<Layout><ApprovedSitePage page="home-clean" /></Layout>} />
          <Route path="/services/deep-reset-clean" element={<Layout><ApprovedSitePage page="deep-reset" /></Layout>} />
          <Route path="/services/airbnb-turnover" element={<Layout><ApprovedSitePage page="airbnb" /></Layout>} />
          <Route path="/services/commercial-cleaning" element={<Layout><ApprovedSitePage page="commercial" /></Layout>} />
          <Route path="/services/move-in-move-out" element={<Layout><ApprovedSitePage page="move" /></Layout>} />
          <Route path="/services/:slug" element={<Layout><ServiceDetailPage /></Layout>} />
          <Route path="/consultation" element={<Layout><ConsultationPage /></Layout>} />
          <Route path="/booking" element={<Layout><BookingPage /></Layout>} />
          <Route path="/booking/success" element={<Layout><BookingSuccessPage /></Layout>} />
          {/* Unlinked from public navigation (kept reachable by direct URL) */}
          <Route path="/service-area" element={<Layout><ServiceAreaPage /></Layout>} />
          <Route path="/reviews" element={<Layout><ReviewsPage /></Layout>} />
          <Route path="/about" element={<Layout><ApprovedSitePage page="about" /></Layout>} />
          <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
          <Route path="/contact" element={<Layout><ApprovedSitePage page="contact" /></Layout>} />
          <Route path="/careers" element={<Layout><CareersPage /></Layout>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<Layout><BookingPage /></Layout>} />
          <Route path="/policies" element={<Layout><PoliciesPage /></Layout>} />
          <Route path="/r/:code" element={<ReferralLandingPage />} />
          <Route path="/terms" element={<Layout><TermsOfServicePage /></Layout>} />
          <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
          <Route path="/sms-terms" element={<Layout><SmsTermsPage /></Layout>} />

          {/* Auth Redirect */}
          <Route path="/dashboard" element={<AuthRedirect />} />

          {/* Universal account settings (any signed-in user) */}
          <Route path="/account" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
          <Route path="/account/settings" element={<Navigate to="/account" replace />} />

          {/* Public card-capture link (no auth needed — token-protected) */}
          <Route path="/card-capture/:token" element={<CardCapturePage />} />
          {/* Public "Tell Us About Your Space" intake (token-protected) */}
          <Route path="/intake/complete" element={<Layout><IntakeCompletePage /></Layout>} />
          <Route path="/intake/:token" element={<IntakePage />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/appointments" element={<AdminRoute><AdminAppointmentsPage /></AdminRoute>} />
          <Route path="/admin/appointments/:id" element={<AdminRoute><AdminAppointmentDetailPage /></AdminRoute>} />
          <Route path="/admin/qc-templates" element={<AdminRoute><AdminQCTemplatesPage /></AdminRoute>} />
          <Route path="/admin/clients" element={<AdminRoute><AdminClientsPage /></AdminRoute>} />
          <Route path="/admin/cleaners" element={<AdminRoute><AdminCleanersPage /></AdminRoute>} />
          <Route path="/admin/services" element={<AdminRoute><AdminServicesPage /></AdminRoute>} />
          <Route path="/admin/invoices" element={<AdminRoute><AdminInvoicesPage /></AdminRoute>} />
          <Route path="/admin/recurring-invoices" element={<AdminRoute><AdminRecurringInvoicesPage /></AdminRoute>} />
          <Route path="/admin/sent-links" element={<AdminRoute><AdminSentLinksPage /></AdminRoute>} />
          <Route path="/admin/time-tracking" element={<AdminRoute><AdminTimeTrackingPage /></AdminRoute>} />
          <Route path="/admin/payouts" element={<AdminRoute><AdminPayoutsPage /></AdminRoute>} />
          <Route path="/admin/analytics" element={<AdminRoute><AdminAnalyticsPage /></AdminRoute>} />
          <Route path="/admin/change-requests" element={<AdminRoute><AdminChangeRequestsPage /></AdminRoute>} />
          <Route path="/admin/service-areas" element={<AdminRoute><AdminServiceAreasPage /></AdminRoute>} />
          <Route path="/admin/reviews" element={<AdminRoute><AdminReviewsPage /></AdminRoute>} />
          <Route path="/admin/faqs" element={<AdminRoute><AdminFAQsPage /></AdminRoute>} />
          <Route path="/admin/duplicates" element={<AdminRoute><AdminDuplicatesPage /></AdminRoute>} />
          <Route path="/admin/settings/notifications" element={<AdminRoute><AdminNotificationSettingsPage /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
          <Route path="/admin/recycle-bin" element={<AdminRoute><AdminRecycleBinPage /></AdminRoute>} />
          <Route path="/admin/inventory" element={<AdminRoute><AdminInventoryPage /></AdminRoute>} />
          <Route path="/admin/inventory/product/:id" element={<AdminRoute><AdminInventoryDetailPage kind="product" /></AdminRoute>} />
          <Route path="/admin/inventory/equipment/:id" element={<AdminRoute><AdminInventoryDetailPage kind="equipment" /></AdminRoute>} />
          <Route path="/admin/settings/sign-in-activity" element={<AdminRoute><AdminSignInActivityPage /></AdminRoute>} />
          <Route path="/admin/schedule" element={<AdminRoute><AdminSchedulePage /></AdminRoute>} />
          <Route path="/admin/calendar" element={<AdminRoute><AdminCalendarPage /></AdminRoute>} />
          <Route path="/admin/hiring" element={<AdminRoute><AdminHiringPage /></AdminRoute>} />
          <Route path="/admin/inquiries" element={<AdminRoute><AdminInquiriesPage /></AdminRoute>} />
          <Route path="/admin/inquiries/:id" element={<AdminRoute><AdminInquiryPage /></AdminRoute>} />
          <Route path="/admin/emails" element={<AdminRoute><AdminEmailActivityPage /></AdminRoute>} />
          <Route path="/admin/emails/failed" element={<AdminRoute><AdminFailedEmailsPage /></AdminRoute>} />
          <Route path="/admin/hiring/:id" element={<AdminRoute><AdminApplicantPage /></AdminRoute>} />
          <Route path="/admin/careers" element={<Navigate to="/admin/hiring" replace />} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/admin/audiences" element={<AdminRoute><AdminAudiencesPage /></AdminRoute>} />
          <Route path="/admin/communication" element={<AdminRoute><AdminCommunicationPage /></AdminRoute>} />
          <Route path="/admin/my-guru" element={<AdminRoute><MyGuruPage /></AdminRoute>} />
          <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
          <Route path="/admin/performance" element={<AdminRoute><AdminPerformancePage /></AdminRoute>} />
          <Route path="/admin/quote-requests" element={<AdminRoute><AdminQuoteRequestsPage /></AdminRoute>} />
          <Route path="/admin/customers" element={<AdminRoute><AdminClientsPage /></AdminRoute>} />
          <Route path="/admin/customers/:userId" element={<AdminRoute><AdminCustomerDetailPage /></AdminRoute>} />
          <Route path="/admin/integrations" element={<AdminRoute><AdminIntegrationsPage /></AdminRoute>} />
          <Route path="/admin/needs-review" element={<AdminRoute><AdminNeedsReviewPage /></AdminRoute>} />
          <Route path="/admin/tasks" element={<AdminRoute><AdminTasksPage /></AdminRoute>} />
          <Route path="/admin/billing" element={<AdminRoute><AdminBillingOverviewPage /></AdminRoute>} />
          <Route path="/admin/billing/payments" element={<AdminRoute><AdminBillingPaymentsPage /></AdminRoute>} />
          <Route path="/admin/schedule/day" element={<AdminRoute><AdminScheduleDayPage /></AdminRoute>} />
          <Route path="/admin/schedule/changes" element={<AdminRoute><AdminScheduleChangesPage /></AdminRoute>} />
          {['expenses', 'cash-flow', 'services', 'data-checks'].map((v) => (
            <Route key={v} path={`/admin/analytics/${v}`} element={<AdminRoute><AdminAnalyticsInsightsPage view={v} /></AdminRoute>} />
          ))}
          <Route path="/admin/ai-workspace" element={<AdminRoute><AdminAIWorkspacePage /></AdminRoute>} />
          <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLogPage /></AdminRoute>} />
          <Route path="/admin/data-cleanup" element={<AdminRoute><AdminDataCleanupPage /></AdminRoute>} />
          <Route path="/admin/reports" element={<AdminRoute><AdminReportsListPage /></AdminRoute>} />
          <Route path="/admin/appointments/:appointmentId/report-review" element={<AdminRoute><AdminReportReviewPage /></AdminRoute>} />
          <Route path="/admin/appointments/:appointmentId/condition-assessment" element={<AdminRoute><ConditionAssessmentPage /></AdminRoute>} />

          {/* Cleaner Routes */}
          <Route path="/cleaner" element={<Navigate to="/cleaner/dashboard" replace />} />
          <Route path="/cleaner/dashboard" element={<CleanerRoute><CleanerShell><GuruTodayPage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/schedule" element={<CleanerRoute><CleanerShell><GuruSchedulePage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/jobs" element={<CleanerRoute><CleanerShell><GuruJobsPage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/job/:id" element={<CleanerRoute><CleanerShell><GuruJobsPage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/messages" element={<CleanerRoute><CleanerShell><GuruMessagesPage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/time" element={<CleanerRoute><CleanerShell><GuruTimePage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/payouts" element={<CleanerRoute><CleanerShell><GuruPayoutsPage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/availability" element={<CleanerRoute><CleanerShell><GuruAvailabilityPage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/account" element={<CleanerRoute><CleanerShell><GuruAccountPage /></CleanerShell></CleanerRoute>} />
          <Route path="/cleaner/performance" element={<CleanerRoute><CleanerPerformancePage /></CleanerRoute>} />
          <Route path="/cleaner/job/:jobId/report" element={<CleanerRoute><CleanerReportPage /></CleanerRoute>} />

          {/* Client Portal Routes */}
          <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="/portal/dashboard" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
          <Route path="/portal/appointments" element={<ClientRoute><ClientCleansPage /></ClientRoute>} />
          <Route path="/portal/appointments/:id" element={<ClientRoute><ClientCleansPage /></ClientRoute>} />
          <Route path="/portal/properties" element={<ClientRoute><ClientPropertiesPage /></ClientRoute>} />
          <Route path="/portal/messages" element={<ClientRoute><ClientMessagesPage /></ClientRoute>} />
          <Route path="/portal/invoices" element={<ClientRoute><ClientBillingPage /></ClientRoute>} />
          <Route path="/portal/profile" element={<ClientRoute><ClientAccountPage /></ClientRoute>} />
          <Route path="/portal/change-requests" element={<Navigate to="/portal/appointments?tab=Requests" replace />} />
          <Route path="/portal/reports" element={<ClientRoute><ClientReportsListPage /></ClientRoute>} />
          <Route path="/portal/reports/:reportId" element={<ClientRoute><ClientReportPage /></ClientRoute>} />

          {/* Legacy routes - redirect to new structure */}
          <Route path="/admin-dashboard" element={<Navigate to="/admin/dashboard" replace />} />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </NotificationProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
